import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  styled
} from "@mui/material";
import { Close as CloseIcon, PhotoCameraBack as PhotoCameraBackIcon } from "@mui/icons-material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import Switch from "@mui/material/Switch";
import FlipCameraIosSharpIcon from "@mui/icons-material/FlipCameraIosSharp";

import {
  setToast,
  setToastMsg,
  setToastType,
} from "../../Slices/toastSlice";
import { CLOUD_FUNCTIONS_ORIGIN } from "../../function-origin";
import { filterEventsByDateAndPermission } from "../ReusableComponents/UtilityFunctions.js";
import usePermissions from "../RolePermission/usePermissions";
import { commonStyle } from "../ReusableComponents/CustomDesignMUI/CustomMUI";
import { renderAsterisk } from "../ReusableComponents/UtilityFunctions";
import { errorMessage } from "../../Services/axiosInstance";
import { editEvent, upsertEvent, upsertEventImage } from "../../Services/EventManageServices";
import { CancleButton, CloseBox, DateTextField, InnerBoxOne, InnerGridFour, InnerGridOne, InnerGridThree, InnerGridTwo, InputTextField, MainBox, MainCard, MainGrid } from "../../Styles/ManageEvents/AddEventStyle";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

function AddEventSlide({
  setChange,
  change,
  onClose,
  isExistingEvent,
  setIsExistingEvent,
  existingEvent,
  setExistingEvent,
  setFilterEvent,
  selectedButton,
  filterEvent
}) {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const user = useSelector((state) => state.selectedActiveView.role);
  const eventData = useSelector((state) => state.adminData.eventData);
  const adminLogin = user === "admin";
  const isMobile = useMediaQuery('(max-width:600px)');
  const isDarkTheme = useSelector((state) => state.theme.isDarkTheme);
  const dayOfWeek = dayjs(new Date()).format("dddd");

  const [eventDetails, setEventDetails] = useState({
    event: eventData ? eventData.event : "",
    // eventDate: eventData ? eventData.eventDate : "",
    eventDate: eventData ? dayjs(eventData.eventDate).isValid() ? dayjs(eventData.eventDate).format("YYYY-MM-DD") : null : null,
    eventDay: eventData && eventData?.eventDay ? eventData?.eventDay : dayOfWeek,
    eventTime: eventData ? eventData.eventTime : "",
    eventDescription: eventData ? eventData.eventDescription : "",
    eventAddress: eventData ? eventData.eventAddress : "",
    eventLocation: eventData ? eventData.eventLocation : "",
  });

  const [error, setError] = useState("");
  const [displayError, setDisplayError] = useState("");
  const [eventMessage, setEventMessage] = useState("");
  const [fromTime, setFromTime] = useState(null);
  const [toTime, setToTime] = useState(null);
  const [isInOffice, setIsInOffice] = useState(true);
  const [choosenEventImageToUpload, setChoosenEventImageToUpload] = useState(
    {}
  );
  const [choosenEventImageToUploadName, setChoosenEventImageToUploadName] =
    useState("");

  const handleFromTimeChange = (time) => {
    setFromTime(time);
  };

  const handleToTimeChange = (time) => {
    setToTime(time);
  };

  const handleCombineTimes = () => {
    if (fromTime && toTime) {
      const fromTimeFormatted = dayjs(fromTime).format("hh:mm A");
      const toTimeFormatted = dayjs(toTime).format("hh:mm A");
      const combinedTime = `${fromTimeFormatted} - ${toTimeFormatted}`;
      setEventDetails((prevState) => ({
        ...prevState,
        eventTime: combinedTime,
      }));
    }
  };

  const handleSwitchChange = () => {
    setIsInOffice((prevIsInOffice) => !prevIsInOffice);
    setEventDetails((prevState) => ({
      ...prevState,
      eventAddress: "",
      eventLocation: "",
    }));
  };

  const dateAndDaySetter = (selectedDate) => {
    const formattedDate = dayjs(selectedDate).format("YYYY-MM-DD");
    const dayOfWeek = dayjs(selectedDate).format("dddd");
    setEventDetails((prevState) => ({
      ...prevState,
      eventDate: formattedDate,
      eventDay: dayOfWeek,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setChoosenEventImageToUpload(file);
      setChoosenEventImageToUploadName(file.name);
    }
  };

  const setClose = () => {
    setError("");
    setDisplayError("");
    setIsExistingEvent(true);
    setExistingEvent({});
    onClose();
  };


  const resetAllData = () => {
    setEventDetails({
      event: "",
      eventDate: null,
      eventDay: "",
      eventTime: "",
      eventDescription: "",
      eventAddress: "",
      eventLocation: "",
    });
    setChoosenEventImageToUpload({});
    setChoosenEventImageToUploadName("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setDisplayError("");

    setError((prevState) => ({
      ...prevState,
      [name]: "",
    }));

    const capitalizedValue = value ? value?.split(" ")?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(" ") : value;
    setEventDetails((prevState) => ({
      ...prevState,
      [name]: capitalizedValue,
    }));
  };

  const addEditEvent = async () => {
    const errors = [];
    if (!eventDetails.event) {
      errors.EventTitle = "Event title is required.";
    }
    if (!eventDetails.eventDate) {
      errors.EventDate = "Please select an event date.";
    }
    if (!eventDetails.eventTime) {
      errors.EventTime = "Please select an event time.";
    }
    if (!eventDetails.eventDescription) {
      errors.eventDescription = "Event description is required.";
    }
    if (!isInOffice) {
      if (
        eventDetails.eventAddress === "" &&
        eventDetails.eventLocation === ""
      ) {
        errors.EventLocation = "Please set the event's address or location.";
      }
    }
    setError(errors);
    const noErrors = Object.keys(errors).length === 0;
    if (Object.keys(errors).length === 0) {
      if (noErrors &&
        Object.keys(choosenEventImageToUpload).length !== 0 &&
        choosenEventImageToUploadName !== "") {
        try {
          const formData = new FormData();
          formData.append("EventImage", choosenEventImageToUpload);
          formData.append("EventImageFileName", choosenEventImageToUploadName);
          const response = await upsertEventImage(choosenEventImageToUploadName, formData);
          if (response.status === 200 || response.status === 201) {
            setChoosenEventImageToUpload({});
            setChoosenEventImageToUploadName("");
            setDisplayError("");
            dispatch(setToast(true));
            dispatch(setToastMsg(response.data.message));
            dispatch(setToastType("success"));
          }
        } catch (error) {
          setDisplayError("Error Uploading Event Image ", error.message);
          return;
        }
      }

      if (noErrors) {
        if (isExistingEvent) {
          try {
            setEventMessage("Updated the Event...")
            const postData = JSON.stringify({ eventDetails, choosenEventImageToUploadName });
            const response = await editEvent(postData)
            if (response.status === 200) {
              let updatedFilterEvent;
              const index = filterEvent?.findIndex((event) => event?._id === eventDetails?._id);
              if (index !== -1) {
                updatedFilterEvent = [...filterEvent];
                updatedFilterEvent[index] = response?.data?.recordAdded;
              } else {
                updatedFilterEvent = [...filterEvent, response?.data?.recordAdded];
              }
              const filteredEvents = filterEventsByDateAndPermission(updatedFilterEvent, selectedButton, hasPermission);
              setFilterEvent(filteredEvents);
              resetAllData();
              if (change === true) {
                setChange(false);
              } else {
                setChange(true);
              }
              setClose();
              setEventMessage("");
              dispatch(setToast(true));
              dispatch(setToastMsg(response.data.message ?? "Event Updated."));
              dispatch(setToastType("success"));
            }
          } catch (error) {
            setDisplayError(errorMessage ?? "");
            setEventMessage("");
          }
        } else {
          try {
            setEventMessage("Added the Event...");
            const postData = JSON.stringify({ eventDetails, choosenEventImageToUploadName });
            const response = await upsertEvent(postData)
            if (response?.status === 200) {
              const data = [response?.data?.recordAdded, ...filterEvent]
              const filteredEvents = filterEventsByDateAndPermission(data, selectedButton, hasPermission);
              setFilterEvent(filteredEvents);
              resetAllData();
              if (change === true) {
                setChange(false);
              } else {
                setChange(true);
              }
              setClose();
              setEventMessage("");
              dispatch(setToast(true));
              dispatch(setToastMsg(response.data.message ?? "Event added."));
              dispatch(setToastType("success"));
            }
          } catch (error) {
            setDisplayError(errorMessage ?? "");
            setEventMessage("");
          }
        }
      }
    } else {
      return;
    }
  };

  useEffect(() => {
    if (fromTime !== "" && toTime !== "") {
      handleCombineTimes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromTime, toTime]);

  useEffect(() => {
    if (isExistingEvent) {
      if (existingEvent) {
        // date convertion
        const edate = dayjs(
          new Date(existingEvent.date)
            .toLocaleDateString("en-GB")
            .split("/")
            .reverse()
            .join("-")
        );
        const { $W } = edate;
        const dayOfWeekFullName = [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ][$W];
        const [startTime, endTime] = existingEvent?.time?.split(" - ");
        setFromTime(
          dayjs(new Date(`${existingEvent?.date} ${startTime}`)?.toISOString())
        );
        setToTime(
          dayjs(new Date(`${existingEvent?.date} ${endTime}`)?.toISOString())
        );
        if (
          Object.keys(existingEvent).includes("address") &&
          Object.keys(existingEvent).includes("location")
        ) {
          if (existingEvent.address !== "" || existingEvent.location !== "") {
            handleSwitchChange();
          }
        }
        setEventDetails((prevState) => ({
          ...prevState,
          _id: existingEvent._id,
          event: existingEvent.eventTitle,
          eventDate: edate,
          eventDay: dayOfWeekFullName,
          eventTime: existingEvent.time,
          eventDescription: existingEvent.description,
          eventAddress: existingEvent.address ? existingEvent.address : "",
          eventLocation: existingEvent.location ? existingEvent.location : "",
        }));
        setChoosenEventImageToUploadName(existingEvent.eventImageName);
      } else {
        resetAllData();
      }
    }
  }, [isExistingEvent, existingEvent]);

  return (
    <Box sx={{ padding: "22px 4px 8px 10px", height: "container" }}>
      <MainBox>
        {/* Box for items on the left */}
        <Box sx={{ padding: "5px 0px 5px 18px" }}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {adminLogin
              ? isExistingEvent
                ? "Edit Event"
                : "Add Event"
              : "Event"}
          </Typography>
        </Box>
        <CloseBox>
          <IconButton onClick={setClose}>
            <CloseIcon color="primary" />
          </IconButton>
        </CloseBox>
      </MainBox>

      <Box sx={{ padding: "8px 4px 8px 10px", marginRight: "8px" }}>
        <MainCard>
          <CardContent>
            <Grid container spacing={2}>
              {isExistingEvent ? (
                <>
                  {choosenEventImageToUploadName === "" ? (
                    <></>
                  ) : (
                    <MainGrid
                      item
                      sm={12}
                    >
                      <img
                        alt="notfound"
                        style={{
                          borderRadius: "5%",
                          backgroundColor: "var(--primary-highlight-color)",
                          padding: "10px",
                          border: "2px solid var(--secondary-text-color)",
                          width: "200px",
                          height: "auto",
                        }}
                        src={`${CLOUD_FUNCTIONS_ORIGIN}/admin/EventImage/${choosenEventImageToUploadName}`}
                      />
                    </MainGrid>
                  )}
                  {adminLogin ? (
                    <InnerGridOne
                      item
                      sm={12}
                      xs={12}
                    >
                      <Tooltip
                        title={"Upload Event Image"}
                        placement="right"
                        arrow
                      >
                        <Button
                          color="primary"
                          component="label"
                          role={undefined}
                          variant="text"
                          tabIndex={-1}
                        >
                          <FlipCameraIosSharpIcon sx={{ fontSize: "30px" }} />
                          <VisuallyHiddenInput
                            type="file"
                            onChange={handleFileChange}
                            accept="image/*,.jpg,.jpeg"
                          />
                        </Button>
                      </Tooltip>
                      <Typography color="primary">
                        {choosenEventImageToUploadName}
                      </Typography>
                    </InnerGridOne>
                  ) : (
                    <></>
                  )}
                </>
              ) : (
                <>
                  {adminLogin ? (
                    <Grid item sm={12} xs={12} sx={{ textAlign: "center" }}>
                      <Tooltip
                        title={"Upload Event Image"}
                        placement="right"
                        arrow
                      >
                        <Button
                          color="primary"
                          component="label"
                          role={undefined}
                          variant="text"
                          tabIndex={-1}
                        >
                          <PhotoCameraBackIcon sx={{ fontSize: "30px" }} />
                          <VisuallyHiddenInput
                            type="file"
                            onChange={handleFileChange}
                            accept="image/*,.jpg,.jpeg"
                          />
                        </Button>
                      </Tooltip>
                      <Typography color="primary">
                        {choosenEventImageToUploadName}
                      </Typography>
                    </Grid>
                  ) : (
                    <></>
                  )}
                </>
              )}

              <Grid item sm={12} xs={12}>
                <InputTextField
                  fullWidth
                  name="event"
                  value={eventDetails?.event}
                  onChange={handleChange}
                  disabled={!adminLogin}
                  label={
                    <>
                      Event Title
                      {renderAsterisk()}
                    </>
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <Typography color="error">{error.EventTitle}</Typography>
              </Grid>

              <Grid item sm={6} xs={12}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="en-gb"
                >
                  <DemoContainer components={["DatePicker"]}>
                    {isMobile ? <DateTextField
                      label={
                        <>
                          Event Date
                          {renderAsterisk()}
                        </>
                      }
                      isdarktheme={isDarkTheme ? "true" : "false"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                          </InputAdornment>
                        ),
                      }}
                      name="eventDate"
                      value={eventDetails.eventDate ? dayjs(eventDetails.eventDate).format("YYYY-MM-DD") : null}
                      onChange={(e) => {
                        dateAndDaySetter(e.target.value);
                        setDisplayError("")
                        setError((prevState) => ({
                          ...prevState,
                          [e.target.name]: "",
                        }));
                      }}
                      disablePast={true}
                      disabled={!adminLogin}
                      type="date"
                    /> : <DatePicker
                      sx={{
                        width: "-webkit-fill-available",
                        ...commonStyle
                      }}
                      slotProps={{
                        openPickerButton: {
                          color: "primary",
                        },

                      }}
                      label={
                        <>
                          Event Date
                          {renderAsterisk()}
                        </>
                      }
                      name="eventDate"
                      value={eventDetails.eventDate ? dayjs(eventDetails.eventDate) : null}
                      onChange={(date) => {
                        dateAndDaySetter(date);
                        setDisplayError("");
                        setError((prevState) => ({
                          ...prevState,
                          eventDate: "",
                        }));
                      }}
                      disablePast={true}
                      disabled={!adminLogin}
                    />}
                  </DemoContainer>
                </LocalizationProvider>
                <Typography color="error">{error.EventDate}</Typography>
              </Grid>

              <Grid item sm={6} xs={12}>
                <Box>
                  <TextField
                    fullWidth
                    sx={{ marginTop: "8px" }}
                    label="Event Day"
                    name="eventDay"
                    value={eventDetails?.eventDay}
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                    }}
                    focused={eventDetails?.eventDay === "" ? false : true}
                    disabled={!adminLogin}
                  />
                </Box>
              </Grid>
              <InnerGridTwo
                item
                sm={12}
                xs={12}
              >
                <InnerGridThree
                  item
                  sm={12}
                  xs={12}
                >
                  <Grid item sx={{ marginTop: "8px" }} sm={5.8} xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        sx={{ width: "-webkit-fill-available" }}
                        value={fromTime}
                        onChange={handleFromTimeChange}
                        slotProps={{
                          openPickerButton: {
                            color: "primary",
                          },
                        }}
                        disabled={!adminLogin}
                        label={
                          <>
                            Timing From
                            {renderAsterisk()}
                          </>
                        }
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid sx={{ marginTop: "8px" }} item sm={0.5} xs={6}>
                    <hr />
                  </Grid>
                  <Grid item sx={{ marginTop: "8px" }} sm={5.8} xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        sx={{ width: "-webkit-fill-available" }}
                        label="Timing To"
                        value={toTime}
                        onChange={handleToTimeChange}
                        slotProps={{
                          openPickerButton: {
                            color: "primary",
                          },
                        }}
                        disabled={!adminLogin}
                      />
                    </LocalizationProvider>
                  </Grid>
                </InnerGridThree>
                <Typography color="error">{error.EventTime}</Typography>
              </InnerGridTwo>

              <Grid sx={{ marginTop: "8px" }} item sm={12} xs={12}>
                <InputTextField
                  fullWidth
                  multiline
                  maxRows={12}
                  name="eventDescription"
                  value={eventDetails?.eventDescription}
                  onChange={handleChange}
                  disabled={!adminLogin}
                  label={
                    <>
                      Event Description
                      {renderAsterisk()}
                    </>
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <Typography color="error">{error.eventDescription}</Typography>
              </Grid>

              <InnerGridFour
                item
                sm={12}
                xs={12}
              >
                <Typography
                  fontWeight={isInOffice ? "bold" : ""}
                  color={isInOffice ? "primary" : "var(--secondary-text-color)"}
                >
                  In Office
                </Typography>
                <Switch
                  checked={!isInOffice}
                  onChange={handleSwitchChange}
                  disabled={!adminLogin}
                />
                <Typography
                  fontWeight={!isInOffice ? "bold" : ""}
                  color={
                    !isInOffice ? "primary" : "var(--secondary-text-color)"
                  }
                >
                  Outside Office
                </Typography>
              </InnerGridFour>

              {!isInOffice ? (
                <>
                  <Grid item sm={12} xs={12}>
                    <InputTextField
                      fullWidth
                      multiline
                      maxRows={12}
                      label="Event Address"
                      name="eventAddress"
                      value={eventDetails?.eventAddress}
                      onChange={handleChange}
                      disabled={!adminLogin}
                    />
                  </Grid>
                  <Grid item sm={12} xs={12}>
                    <InputTextField
                      fullWidth
                      multiline
                      maxRows={12}
                      name="eventLocation"
                      value={eventDetails?.eventLocation}
                      onChange={handleChange}
                      disabled={!adminLogin}
                      label={
                        <>
                          Event Location
                          {renderAsterisk()}
                        </>
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <Typography color="error">{error.EventLocation}</Typography>
                  </Grid>{" "}
                </>
              ) : (
                <></>
              )}
            </Grid>

            <InnerBoxOne mt={2}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {eventMessage && (
                  <Typography
                    sx={{
                      mr: 2,
                      color: isDarkTheme ? "#e0e0e0" : "#5e6e82",
                    }}
                  >
                    {eventMessage}
                  </Typography>
                )}
                {displayError && (
                  <Typography sx={{ mr: 2, color: "red" }}>{displayError}</Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CancleButton onClick={setClose}>
                  Cancel
                </CancleButton>
                {isExistingEvent ? (
                  adminLogin ? (
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => {
                        addEditEvent();
                      }}
                    >
                      Update event
                    </Button>
                  ) : (
                    <></>
                  )
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      addEditEvent();
                    }}
                  >
                    Add event
                  </Button>
                )}
              </Box>
            </InnerBoxOne>

          </CardContent>
        </MainCard>
      </Box>
    </Box>
  );
}

export default AddEventSlide;
